import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Box,
  MenuItem,
  Grid,
  Checkbox,
  TableCell,
  TableRow,
  Typography,
  createFilterOptions,
  TextField,
} from '@mui/material'

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { StateSchema } from '@/hooks/form/models'
import keys from '@/utils/keys'

import {
  BonusTriggerType,
  TriggerConditions,
  PaymentMethods,
  CurrencyCodeEnum,
} from '@/enums'
import AutocompleteCustom from '@/views/components/autocomplete-custom/index.component'
import NumberFormatCustom from '@/views/components/input-number-format/index.component'

import { authSelector } from '@/state/auth/slices/index.slices'
import CurrenciesTableComponent from '@/views/components/currencies-table/currencies-table.components'

type Props = {
  handleOnChange: (name: string, value: any) => void
  handleOnChangePMethods: (name: string, values: any) => void
  state: StateSchema
}

const BonusTriggerComponent = ({
  handleOnChange,
  state,
  handleOnChangePMethods,
}: Props) => {
  const { t } = useTranslation()

  const { REACT_APP_STAGE: env } = process.env

  const { brand } = useSelector(authSelector)

  const currencyHeadCells = ['baseCurrency', 'amount']

  const bonusTriggerTypes = [
    ...keys(BonusTriggerType)
      .filter(
        (item) =>
          typeof BonusTriggerType[item] !== 'string' &&
          (BonusTriggerType[item] === BonusTriggerType.DEPOSIT_BY_CLICK ||
            BonusTriggerType[item] === BonusTriggerType.DEPOSIT_BY_CODE ||
            BonusTriggerType[item] === BonusTriggerType.GAMIFICATION ||
            BonusTriggerType[item] === BonusTriggerType.BY_REGISTRATION ||
            BonusTriggerType[item] === BonusTriggerType.BY_CODE ||
            BonusTriggerType[item] === BonusTriggerType.BY_BUNDLE),
      )

      //       .filter(
      //   (item) =>
      //     (env === 'prod' &&
      //       typeof BonusTriggerType[item] !== 'string' &&
      //       BonusTriggerType[item] !== BonusTriggerType.MANUAL &&
      //       BonusTriggerType[item] !== BonusTriggerType.GAMIFICATION &&
      //       BonusTriggerType[item] !== BonusTriggerType.BY_REGISTRATION &&
      //       BonusTriggerType[item] !== BonusTriggerType.BY_CODE) ||
      //     (env === 'stg' &&
      //       typeof BonusTriggerType[item] !== 'string' &&
      //       BonusTriggerType[item] !== BonusTriggerType.MANUAL),
      // )
      .map((x) => {
        return {
          index: BonusTriggerType[x],
          label: x,
        }
      }),
  ].sort((a, b) => a.label.localeCompare(b.label))

  const conditionsTypes = [
    ...keys(TriggerConditions).map((x) => {
      return {
        index: TriggerConditions[x],
        label: x,
      }
    }),
  ].sort((a, b) => a.label.localeCompare(b.label))

  const paymentMethodsTypes = [
    ...keys(PaymentMethods)
      .filter((item) => typeof PaymentMethods[item] !== 'string')
      .map((x) => {
        return {
          index: PaymentMethods[x],
          label: t(`paymentMethodsTypes.${x}`),
        }
      }),
  ].sort((a, b) => a.label.localeCompare(b.label))

  const allSelected =
    paymentMethodsTypes.length === state.paymentMethods.value.length

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      handleOnChangePMethods('paymentMethods', paymentMethodsTypes)
    } else {
      handleOnChangePMethods('paymentMethods', [])
    }
  }

  const handleToggleSelectAll = () => {
    handleSelectAll && handleSelectAll(!allSelected)
  }

  const handleChange = (event: any, selectedOptions: any[], reason: string) => {
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (selectedOptions.find((option) => option.value === 'select-all')) {
        handleToggleSelectAll()
      } else if (reason === 'removeOption') {
        handleOnChangePMethods('paymentMethods', selectedOptions)
      } else {
        const findValue =
          selectedOptions.length > 0 &&
          state.paymentMethods.value.find(
            (item: any) =>
              item.label === selectedOptions[selectedOptions.length - 1].label,
          )

        if (findValue) {
          const removeValue = state.paymentMethods.value.filter(
            (provider: any) =>
              provider.label !==
              selectedOptions[selectedOptions.length - 1].label,
          )
          handleOnChangePMethods('paymentMethods', removeValue)
        } else {
          handleOnChangePMethods('paymentMethods', selectedOptions)
        }
      }
    } else if (reason === 'clear') {
      handleOnChangePMethods('paymentMethods', [])
    }
  }

  const filter = createFilterOptions()

  return (
    <Box maxWidth={800}>
      <Box maxWidth={400}>
        <Typography fontWeight={700} sx={{ mb: '20px' }}>
          {t('triggerTypelabel')}
        </Typography>
        <TextField
          fullWidth
          name="triggerBy"
          value={state.triggerBy.value || ''}
          label={t('labels.triggerType')}
          onChange={(e: any) => handleOnChange('triggerBy', e.target.value)}
          defaultValue=""
          select
        >
          {(bonusTriggerTypes || []).map((item) => (
            <MenuItem key={item.index} value={item.index}>
              {t(`bonusTriggerTypes.${item.label}`)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {(state.triggerBy.value === BonusTriggerType.DEPOSIT_BY_CODE ||
        state.triggerBy.value === BonusTriggerType.DEPOSIT_BY_CLICK ||
        state.triggerBy.value === BonusTriggerType.BY_CODE ||
        state.triggerBy.value === BonusTriggerType.BY_REGISTRATION) && (
        <>
          <Box mt={3}>
            <Grid container>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="condition"
                  label={t('condition')}
                  value={state.condition.value || ''}
                  onChange={(e: any) =>
                    handleOnChange('condition', e.target.value)
                  }
                  select
                >
                  {(conditionsTypes || []).map((item) => (
                    <MenuItem key={item.index} value={item.label}>
                      {t(`conditionsTypes.${item.index.toUpperCase()}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4} sx={{ marginLeft: '15px' }}>
                <NumberFormatCustom
                  name="depositCount"
                  label={t('depositCount')}
                  handleOnChange={handleOnChange}
                  state={state}
                  prefix=""
                  disableInputPropsAdornment
                  fullwidth
                />
              </Grid>
            </Grid>
          </Box>
          {state.triggerBy.value !== BonusTriggerType.BY_CODE && (
            <>
              <Box mb={2} mt={3}>
                <Typography fontWeight={700} gutterBottom sx={{ mb: '15px' }}>
                  {t('minimumAmount')}
                </Typography>
                <CurrenciesTableComponent currencyHeadCells={currencyHeadCells}>
                  {brand.currencies.map((currency) => (
                    <TableRow key={`trigger_${currency}`}>
                      <TableCell>
                        {currency === CurrencyCodeEnum.USD
                          ? `${currency} ${t('labels.currencyDefault')}`
                          : currency}
                      </TableCell>
                      <TableCell sx={{ width: 400 || 'auto' }}>
                        <NumberFormatCustom
                          name={`minimumDepositAmount_${currency}`}
                          handleOnChange={handleOnChange}
                          state={state}
                          currency={currency}
                          style={{ padding: '16px 0' }}
                          prefix=""
                          fullwidth
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </CurrenciesTableComponent>
              </Box>
              <Grid container>
                <Grid item xs={6}>
                  <AutocompleteCustom
                    getOptionLabel={(option) => option.label}
                    filterOptions={(options: any, params: any) => {
                      const filtered = filter(options, params)
                      return [
                        { label: t('labels.selectAll'), value: 'select-all' },
                        ...filtered,
                      ]
                    }}
                    limitTags={2}
                    multiple
                    onChange={handleChange}
                    chipTags
                    options={paymentMethodsTypes}
                    label={t('paymentMethods')}
                    value={state.paymentMethods.value || []}
                    renderOption={(props, option) => {
                      const selectAllProps =
                        option.value === 'select-all' // To control the state of 'select-all' checkbox
                          ? { checked: allSelected }
                          : {}

                      return (
                        <li {...props}>
                          <Checkbox
                            color="primary"
                            icon={<CheckBoxOutlineBlank fontSize="small" />}
                            checkedIcon={<CheckBox fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={
                              state.paymentMethods.value &&
                              state.paymentMethods.value.filter(
                                (paymentMethod: any) =>
                                  paymentMethod.index === option.index,
                              ).length > 0
                            }
                            {...selectAllProps}
                          />
                          {option.value === 'select-all' ? (
                            <span className="select-all-items">
                              <em>{option.label}</em>
                            </span>
                          ) : (
                            option.label
                          )}
                        </li>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  )
}

export default BonusTriggerComponent
